import { useMemo, useRef } from "react";
import { Button } from "primereact/button";
import { TieredMenu } from "primereact/tieredmenu";
import { MenuItem } from "primereact/menuitem";
import { StatusIcon } from "./StatusIcon";
import styles from "./ReportActionMenu.module.css";
import { useUpdateReport, useUsers } from "../data";
import * as client from "../../../client";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { displayNameForUser } from "../../../util";
import { SafeReportMetadataResponse } from "../Conversion";
import { useClientId, useFeaturesForClientId } from "../../../ClientConfig";
import { featureEnabled } from "../../../client-features";
import { confirmDialog } from "primereact/confirmdialog";
import {
  deleteReport,
  sendClearanceEmail,
  getClearanceEmailRecipients,
} from "../../../api/routes";
import { useNavigate } from "react-router-dom";
import { getAllStatuses, isClearanceStatus } from "./util";
import { useToast } from "../../../context-providers/Toast";

type ReportActionMenuProps = {
  report: SafeReportMetadataResponse;
  isUpcomingRenewal?: boolean;
  setActionMenuIsOpen?: (value: boolean) => void;
  raised?: boolean;
};

const INTERNAL_DOMAIN = "@luminorrisk.com";

const isExternalEmail = (email: string): boolean => {
  return !email.toLowerCase().endsWith(INTERNAL_DOMAIN);
};

const userListWithSeparators = (
  allUsers: client.User[],
  title: string,
  filterLogic: (user: client.User) => boolean,
  onSelect: (userId: string | null) => void
) => {
  const underwriters = allUsers.filter(filterLogic);
  const others = allUsers.filter((user) => !filterLogic(user));

  const items: MenuItem[] = [];

  // Add underwriters section
  if (underwriters.length > 0) {
    items.push({
      label: title,
      disabled: true,
      className: styles.header,
    });
  }
  items.push(
    ...underwriters.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add separator if both sections exist
  if (underwriters.length > 0 && others.length > 0) {
    items.push({ separator: true });
  }

  // Add others section (including null option)
  if (others.length > 0) {
    items.push({
      label: "Other Users",
      disabled: true,
      className: styles.header,
    });
  }

  items.push(
    ...others.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add null option to others section
  items.push({
    icon: <PantheonAvatarWithoutMenu label={undefined} showName={true} />,
    command: () => {
      onSelect(null);
    },
  });

  return items;
};

const RecipientList: React.FC<{ recipients: string[] }> = ({ recipients }) => {
  if (recipients.length === 0) {
    return (
      <div style={{ color: "#888", fontStyle: "italic", padding: "8px 0" }}>
        No recipients found for this submission.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        This email will be sent to:
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {recipients.map((email) => {
          const external = isExternalEmail(email);
          return (
            <div
              key={email}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i
                className={external ? "pi pi-globe" : "pi pi-building"}
                style={{
                  fontSize: "12px",
                  color: external ? "var(--orange-500)" : "var(--green-500)",
                }}
              />
              <span>{email}</span>
              {external && (
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "var(--orange-500)",
                    backgroundColor: "var(--surface-100)",
                    padding: "1px 6px",
                    borderRadius: "4px",
                  }}
                >
                  External
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ReportActionMenu: React.FC<ReportActionMenuProps> = ({
  report,
  isUpcomingRenewal,
  setActionMenuIsOpen,
  raised = false,
}) => {
  const clientId = useClientId();
  const users = useUsers().data;
  const lobUsers =
    users?.all_company_users.filter((u) => u.lobs.includes(report.lob)) ?? [];
  const menu = useRef<TieredMenu>(null);
  const navigate = useNavigate();
  const features = useFeaturesForClientId(clientId, navigate);
  const updateReport = useUpdateReport(report.report_id);
  const toast = useToast();

  const items = useMemo(() => {
    const baseItems: MenuItem[] = [
      {
        label: "Update status",
        icon: "pi pi-file-edit",
        items: getAllStatuses().map(
          (status): MenuItem => ({
            icon: <StatusIcon status={status} />,
            command: async () => {
              await updateReport.mutateAsync({
                status: { new_status: status },
              });
              if (isClearanceStatus(status)) {
                // Fetch recipients before showing the dialog
                let recipients: string[] = [];
                try {
                  const result = await getClearanceEmailRecipients(
                    report.report_id
                  );
                  recipients = result.recipients;
                } catch {
                  // If we can't fetch recipients, still show the dialog
                }

                confirmDialog({
                  message: <RecipientList recipients={recipients} />,
                  pt: { message: { style: { width: "100%" } } },
                  style: { width: "480px" },
                  header: "Send Clearance Email",
                  acceptLabel: "Send",
                  rejectLabel: "Cancel",
                  accept: async () => {
                    try {
                      await sendClearanceEmail(report.report_id, status);
                      toast.show({
                        severity: "success",
                        summary: "Email Sent",
                        detail: `Clearance email sent to ${recipients.length} recipient(s)`,
                      });
                    } catch {
                      toast.show({
                        severity: "error",
                        summary: "Email Failed",
                        detail: "Failed to send clearance email",
                      });
                    }
                  },
                });
              }
            },
          })
        ),
      },
      {
        label: featureEnabled(clientId, "assignee-named-underwriter")
          ? "Update underwriter"
          : "Update assignee",
        icon: "pi pi-user-edit",
        items: featureEnabled(clientId, "underwriter-handler-separation")
          ? userListWithSeparators(
              lobUsers,
              "Underwriters",
              () => true,
              (userId) =>
                updateReport.mutate({
                  assignee: { new_assignee: userId },
                })
            )
          : (lobUsers as (client.User | null)[]).concat([null]).map((user) => {
              const label = user ? displayNameForUser(user) : undefined;
              return {
                icon: (
                  <PantheonAvatarWithoutMenu label={label} showName={true} />
                ),
                command: () => {
                  updateReport.mutate({
                    assignee: { new_assignee: user ? user.id : null },
                  });
                },
              };
            }),
      },
      ...(featureEnabled(clientId, "handler-column")
        ? [
            {
              label: "Update specialist",
              icon: "pi pi-users",
              items: featureEnabled(clientId, "underwriter-handler-separation")
                ? userListWithSeparators(
                    lobUsers,
                    "Specialists",
                    () => true,
                    (userId) =>
                      updateReport.mutate({
                        additionalAssignees: {
                          assignees: userId ? { handler: userId } : {},
                        },
                      })
                  )
                : (lobUsers as (client.User | null)[])
                    .concat([null])
                    .map((user) => {
                      const label = user ? displayNameForUser(user) : undefined;
                      return {
                        icon: (
                          <PantheonAvatarWithoutMenu
                            label={label}
                            showName={true}
                          />
                        ),
                        command: () => {
                          updateReport.mutate({
                            additionalAssignees: {
                              assignees: user ? { handler: user.id } : {},
                            },
                          });
                        },
                      };
                    }),
            },
          ]
        : []),
    ];

    if (users?.current_user.is_client_admin) {
      if (
        featureEnabled(clientId, "submission-history", {
          user: users.current_user,
        })
      ) {
        baseItems.push({
          label: "View history",
          icon: "pi pi-history",
          command: () => {
            navigate(`/dashboard/submission-history/${report.report_id}`);
          },
        });
      }
      baseItems.push({
        label: "Delete report",
        icon: "pi pi-trash",
        command: () =>
          confirmDialog({
            message: "Are you sure you want to delete this report?",
            header: "Delete Report",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
              await deleteReport(report.report_id);
              navigate(features.defaultRoute(users.current_user));
              toast.show({
                severity: "success",
                summary: `${report.report_metadata.company_info.company_name} deleted`,
              });
            },
          }),
      });
    }

    return baseItems;
  }, [users, updateReport, isUpcomingRenewal]);

  return (
    <div>
      <TieredMenu
        model={items}
        popup
        ref={menu}
        style={{ width: "232px" }}
        pt={{ submenu: { style: { maxHeight: "600px", overflowY: "auto" } } }}
        breakpoint="767px"
        onShow={() => setActionMenuIsOpen && setActionMenuIsOpen(true)}
        onHide={() => setActionMenuIsOpen && setActionMenuIsOpen(false)}
      />
      <Button
        text
        raised={raised}
        severity="secondary"
        icon="pi pi-ellipsis-v"
        onClick={(e) => {
          menu.current?.toggle(e);
          e.stopPropagation();
        }}
      />
    </div>
  );
};
